import * as THREE from 'three';
import { DecalGeometry } from 'three/addons/geometries/DecalGeometry.js';
import type { CharacterRig, EyeExpression, EyeId, HeadId } from './types';
import { EYE_TUNING, type EyePlacement } from './eye-tuning';
import { resolveVisibleHeadRoot } from './visible-head-root';

const DEFAULT_PAIRED_PLACEMENTS: Required<Pick<EyePlacement, 'anchor' | 'scale' | 'rotation'>>[] = [
  { anchor: [-0.11, 0.02, 0.34], scale: 0.42, rotation: [0, 0, 0] },
  { anchor: [0.11, 0.02, 0.34], scale: 0.42, rotation: [0, 0, 0] },
];
const DEFAULT_CENTER_PLACEMENT: EyePlacement = {
  anchor: [0, 0.02, 0.36],
  scale: 0.48,
  rotation: [0, 0, 0],
};
const EYE_ALPHA_TEST = 0.08;
const EYE_PLACEHOLDER_WIDTH = 0.5;
const EYE_DECAL_DEPTH_FRACTION = 1.1;
const EYE_DECAL_MIN_DEPTH = 0.08;
const EYE_DECAL_SURFACE_INSET_FRACTION = 0.05;
const EYE_SURFACE_AXIS_MIN_LENGTH_SQ = 1e-8;
const EYE_BLINK_INTERVAL_SECONDS = 3.4;
const EYE_BLINK_DURATION_SECONDS = 0.14;
const EYE_BLINK_SCALE_Y_MIN = 0.16;
const EYE_POP_DURATION_SECONDS = 0.16;
const EYE_POP_SCALE = 0.08;
const THREE_EYE_HEAD_IDS = new Set<HeadId>(['alien']);

export interface AttachEyeMeshesArgs {
  rig: CharacterRig;
  headId: HeadId;
  eyeId: EyeId;
  expression: EyeExpression;
  eyeTexture: THREE.Texture;
}

export function attachEyeMeshes(args: AttachEyeMeshesArgs): void {
  const { rig, headId, eyeId, expression, eyeTexture } = args;
  const parent = resolveVisibleHeadRoot(rig) ?? rig.resolveSocket?.('face') ?? rig.root;

  if (canReuseEyeGroup(rig, eyeId, parent, eyeTexture)) {
    updateEyeGroupTexture(rig._eyeGroup!, eyeTexture);
    setEyeGroupExpression(rig._eyeGroup!, eyeId, expression);
    triggerEyePop(rig._eyeGroup!);
    rig._eyeExpression = expression;
    return;
  }

  disposeEyeMeshes(rig);

  const group = new THREE.Group();
  setEyeGroupExpression(group, eyeId, expression);
  group.userData.characterEyes = true;
  group.userData.eyeProjectionMode = 'decal';
  group.userData.eyeTextureAspect = getTextureAspect(eyeTexture);
  group.userData.eyeBlinkClock = 0;
  group.userData.eyeBlinkOffset = blinkOffsetForEyeId(eyeId);
  triggerEyePop(group);

  const placements = getEyePlacements(headId, eyeId);

  const targetMeshes = findEyeTargetMeshes(parent);
  for (const entry of placements) {
    const mesh = createEyeDecal({
      name: entry.name,
      placement: entry.placement,
      texture: eyeTexture,
      parent,
      targetMeshes,
      mirrorU: entry.name === 'right',
    });
    if (mesh != null) group.add(mesh);
  }

  parent.add(group);
  rig._eyeGroup = group;
  rig._eyeExpression = expression;
}

function getEyePlacements(
  headId: HeadId,
  eyeId: EyeId,
): { name: string; placement: EyePlacement }[] {
  if (eyeId === 'cyclops') {
    return [{ name: 'center', placement: EYE_TUNING[headId]?.center ?? DEFAULT_CENTER_PLACEMENT }];
  }

  const paired = [
    {
      name: 'left',
      placement: EYE_TUNING[headId]?.left ?? DEFAULT_PAIRED_PLACEMENTS[0],
    },
    {
      name: 'right',
      placement: EYE_TUNING[headId]?.right ?? DEFAULT_PAIRED_PLACEMENTS[1],
    },
  ];

  if (!THREE_EYE_HEAD_IDS.has(headId)) return paired;

  return [
    ...paired,
    { name: 'center', placement: EYE_TUNING[headId]?.center ?? DEFAULT_CENTER_PLACEMENT },
  ];
}

function canReuseEyeGroup(
  rig: CharacterRig,
  eyeId: EyeId,
  parent: THREE.Object3D,
  texture: THREE.Texture,
): boolean {
  const group = rig._eyeGroup;
  return (
    group != null &&
    group.parent === parent &&
    group.children.length > 0 &&
    group.userData.eyeId === eyeId &&
    group.userData.eyeProjectionMode === 'decal' &&
    Math.abs(Number(group.userData.eyeTextureAspect ?? 1) - getTextureAspect(texture)) < 1e-6
  );
}

function setEyeGroupExpression(group: THREE.Group, eyeId: EyeId, expression: EyeExpression): void {
  group.name = `eyes:${eyeId}:${expression}`;
  group.userData.eyeId = eyeId;
  group.userData.eyeExpression = expression;
}

function updateEyeGroupTexture(group: THREE.Group, texture: THREE.Texture): void {
  group.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const material = child.material;
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) {
      if (mat instanceof THREE.MeshBasicMaterial) {
        mat.map = texture;
        mat.needsUpdate = true;
      }
    }
  });
}

interface CreateEyeDecalArgs {
  name: string;
  placement: EyePlacement;
  texture: THREE.Texture;
  parent: THREE.Object3D;
  targetMeshes: THREE.Mesh[];
  mirrorU: boolean;
}

function createEyeDecal(args: CreateEyeDecalArgs): THREE.Mesh | null {
  const { name, placement, texture, parent, targetMeshes, mirrorU } = args;
  if (targetMeshes.length === 0) return null;

  const { width, height } = computeEyeSize(placement, texture);
  const depth = Math.max(Math.max(width, height) * EYE_DECAL_DEPTH_FRACTION, EYE_DECAL_MIN_DEPTH);

  parent.updateWorldMatrix(true, true);
  for (const mesh of targetMeshes) mesh.updateWorldMatrix(true, false);

  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  getEyeWorldTransform(parent, placement, position, quaternion);

  const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion).normalize();
  const inset = Math.min(placement.scale * EYE_DECAL_SURFACE_INSET_FRACTION, depth * 0.45);
  const surfaceHit = findEyeSurfaceHit(targetMeshes, position, normal, depth);
  const targetMesh = surfaceHit?.object instanceof THREE.Mesh ? surfaceHit.object : targetMeshes[0];
  let decalQuaternion = quaternion;

  if (surfaceHit != null) {
    position.copy(surfaceHit.point);
    const surfaceNormal = getWorldFaceNormal(surfaceHit, normal);
    if (surfaceNormal != null) {
      decalQuaternion = buildSurfaceAlignedEyeQuaternion(quaternion, surfaceNormal);
    }
  } else {
    position.addScaledVector(normal, -inset);
  }

  const orientation = new THREE.Euler().setFromQuaternion(decalQuaternion, 'XYZ');
  const geometry = new DecalGeometry(
    targetMesh,
    position,
    orientation,
    new THREE.Vector3(width, height, depth),
  );
  const positionAttr = geometry.getAttribute('position');
  if (positionAttr == null || positionAttr.count === 0) {
    geometry.dispose();
    return null;
  }

  if (mirrorU) mirrorGeometryU(geometry);
  moveGeometryIntoParentSpace(geometry, parent);

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    alphaTest: EYE_ALPHA_TEST,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = `eye:${name}`;
  mesh.userData.characterEyes = true;
  mesh.userData.eyeMirrorU = mirrorU;
  applyEyeMeshLocalCenter(mesh);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.renderOrder = 30;
  return mesh;
}

function findEyeTargetMeshes(root: THREE.Object3D): THREE.Mesh[] {
  const targetMeshes: THREE.Mesh[] = [];
  root.traverse((child) => {
    if (isCharacterEyeObject(child)) return;
    if (!(child instanceof THREE.Mesh)) return;
    const geometry = child.geometry;
    if (geometry.getAttribute('position') == null) return;
    if (geometry.getAttribute('normal') == null) geometry.computeVertexNormals();
    targetMeshes.push(child);
  });
  return targetMeshes;
}

function isCharacterEyeObject(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object;
  while (current != null) {
    if (current.userData.characterEyes === true) return true;
    current = current.parent;
  }
  return false;
}

function computeEyeSize(
  placement: EyePlacement,
  texture: THREE.Texture,
): { width: number; height: number } {
  const width = Math.max(EYE_PLACEHOLDER_WIDTH * placement.scale, 0.001);
  const height = Math.max(width / getTextureAspect(texture), 0.001);
  return { width, height };
}

function getTextureAspect(texture: THREE.Texture): number {
  const image = texture.image as { width?: unknown; height?: unknown } | null | undefined;
  const width = typeof image?.width === 'number' && image.width > 0 ? image.width : 1;
  const height = typeof image?.height === 'number' && image.height > 0 ? image.height : 1;
  return width / height;
}

function getEyeWorldTransform(
  parent: THREE.Object3D,
  placement: EyePlacement,
  position: THREE.Vector3,
  quaternion: THREE.Quaternion,
): void {
  const probe = new THREE.Object3D();
  probe.position.fromArray(placement.anchor);
  probe.rotation.set(...placement.rotation);
  parent.add(probe);
  probe.updateWorldMatrix(true, false);
  probe.getWorldPosition(position);
  probe.getWorldQuaternion(quaternion);
  probe.removeFromParent();
}

function findEyeSurfaceHit(
  targetMeshes: THREE.Mesh[],
  position: THREE.Vector3,
  normal: THREE.Vector3,
  depth: number,
): THREE.Intersection<THREE.Object3D> | null {
  const raycaster = new THREE.Raycaster(
    position.clone().addScaledVector(normal, depth),
    normal.clone().negate(),
    0,
    depth * 3,
  );
  return raycaster.intersectObjects(targetMeshes, false)[0] ?? null;
}

function getWorldFaceNormal(
  surfaceHit: THREE.Intersection<THREE.Object3D>,
  referenceNormal: THREE.Vector3,
): THREE.Vector3 | null {
  if (surfaceHit.face == null || surfaceHit.object == null) return null;
  const surfaceNormal = surfaceHit.face.normal
    .clone()
    .transformDirection(surfaceHit.object.matrixWorld)
    .normalize();
  if (surfaceNormal.dot(referenceNormal) < 0) surfaceNormal.negate();
  return surfaceNormal;
}

function buildSurfaceAlignedEyeQuaternion(
  userQuaternion: THREE.Quaternion,
  surfaceNormal: THREE.Vector3,
): THREE.Quaternion {
  const zAxis = surfaceNormal.clone().normalize();
  const userXAxis = new THREE.Vector3(1, 0, 0).applyQuaternion(userQuaternion);
  const userYAxis = new THREE.Vector3(0, 1, 0).applyQuaternion(userQuaternion);
  const projectedX = projectAxisToTangent(userXAxis, zAxis);
  const projectedY = projectAxisToTangent(userYAxis, zAxis);

  let yAxis =
    projectedY.lengthSq >= EYE_SURFACE_AXIS_MIN_LENGTH_SQ ? projectedY.axis.clone() : null;
  if (yAxis == null && projectedX.lengthSq >= EYE_SURFACE_AXIS_MIN_LENGTH_SQ) {
    yAxis = new THREE.Vector3().crossVectors(zAxis, projectedX.axis).normalize();
  }
  if (yAxis == null) {
    const fallback =
      Math.abs(zAxis.y) < 0.95 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    yAxis = projectAxisToTangent(fallback, zAxis).axis;
  }
  if (yAxis.dot(userYAxis) < 0) yAxis.negate();

  const xAxis = new THREE.Vector3().crossVectors(yAxis, zAxis).normalize();
  const basis = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
  return new THREE.Quaternion().setFromRotationMatrix(basis);
}

function projectAxisToTangent(
  axis: THREE.Vector3,
  normal: THREE.Vector3,
): { axis: THREE.Vector3; lengthSq: number } {
  const tangent = axis.clone().addScaledVector(normal, -axis.dot(normal));
  const lengthSq = tangent.lengthSq();
  if (lengthSq > EYE_SURFACE_AXIS_MIN_LENGTH_SQ) tangent.normalize();
  return { axis: tangent, lengthSq };
}

function moveGeometryIntoParentSpace(geometry: THREE.BufferGeometry, parent: THREE.Object3D): void {
  parent.updateWorldMatrix(true, false);

  const position = geometry.getAttribute('position');
  const point = new THREE.Vector3();
  for (let index = 0; index < position.count; index += 1) {
    point.fromBufferAttribute(position, index);
    parent.worldToLocal(point);
    position.setXYZ(index, point.x, point.y, point.z);
  }
  position.needsUpdate = true;

  const normal = geometry.getAttribute('normal');
  if (normal != null) {
    const normalWorldToLocal = new THREE.Matrix3().getNormalMatrix(parent.matrixWorld).invert();
    for (let index = 0; index < normal.count; index += 1) {
      point.fromBufferAttribute(normal, index).applyMatrix3(normalWorldToLocal).normalize();
      normal.setXYZ(index, point.x, point.y, point.z);
    }
    normal.needsUpdate = true;
  }

  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  centerGeometryOnMeshOrigin(geometry);
}

function centerGeometryOnMeshOrigin(geometry: THREE.BufferGeometry): void {
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (box == null || box.isEmpty()) return;

  const center = box.getCenter(new THREE.Vector3());
  geometry.translate(-center.x, -center.y, -center.z);
  geometry.userData.eyeLocalCenter = center.toArray();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

function mirrorGeometryU(geometry: THREE.BufferGeometry): void {
  const uv = geometry.getAttribute('uv');
  if (!(uv instanceof THREE.BufferAttribute)) return;
  for (let index = 0; index < uv.count; index += 1) {
    uv.setX(index, 1 - uv.getX(index));
  }
  uv.needsUpdate = true;
}

export function disposeEyeMeshes(rig: CharacterRig): void {
  if (rig._eyeGroup == null) return;
  rig._eyeGroup.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const material = child.material;
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) mat.dispose();
  });
  rig._eyeGroup.removeFromParent();
  rig._eyeGroup.clear();
  rig._eyeGroup = null;
}

export function updateEyeMeshes(rig: CharacterRig, dt: number): void {
  const group = rig._eyeGroup;
  if (group == null) return;
  const safeDt = Number.isFinite(dt) ? Math.min(Math.max(dt, 0), 0.1) : 0;
  group.scale.set(1, 1, 1);
  group.userData.eyeTickClock = Number(group.userData.eyeTickClock ?? 0) + safeDt;

  const expression = group.userData.eyeExpression as EyeExpression | undefined;
  const blinkScaleY = expression === 'dead' ? 1 : updateBlinkScaleY(group, safeDt);
  const popScale = updatePopScale(group, safeDt);

  for (const child of group.children) {
    if (!(child instanceof THREE.Mesh)) continue;
    applyEyeMeshLocalCenter(child);
    child.scale.set(popScale, popScale * blinkScaleY, popScale);
  }
}

function applyEyeMeshLocalCenter(mesh: THREE.Mesh): void {
  const center = mesh.geometry.userData.eyeLocalCenter;
  if (
    Array.isArray(center) &&
    center.length === 3 &&
    center.every((value) => typeof value === 'number' && Number.isFinite(value))
  ) {
    mesh.position.set(center[0], center[1], center[2]);
  }
}

function triggerEyePop(group: THREE.Group): void {
  group.userData.eyePopClock = EYE_POP_DURATION_SECONDS;
}

function blinkOffsetForEyeId(eyeId: EyeId): number {
  let hash = 0;
  for (let index = 0; index < eyeId.length; index += 1) {
    hash = (hash * 31 + eyeId.charCodeAt(index)) % 997;
  }
  return (hash / 997) * EYE_BLINK_INTERVAL_SECONDS;
}

function updateBlinkScaleY(group: THREE.Group, dt: number): number {
  const blinkClock =
    (Number(group.userData.eyeBlinkClock ?? 0) + dt + Number(group.userData.eyeBlinkOffset ?? 0)) %
    EYE_BLINK_INTERVAL_SECONDS;
  group.userData.eyeBlinkClock = blinkClock;
  group.userData.eyeBlinkOffset = 0;
  if (blinkClock >= EYE_BLINK_DURATION_SECONDS) return 1;

  const t = blinkClock / EYE_BLINK_DURATION_SECONDS;
  return EYE_BLINK_SCALE_Y_MIN + Math.abs(t - 0.5) * 2 * (1 - EYE_BLINK_SCALE_Y_MIN);
}

function updatePopScale(group: THREE.Group, dt: number): number {
  const popClock = Math.max(0, Number(group.userData.eyePopClock ?? 0) - dt);
  group.userData.eyePopClock = popClock;
  if (popClock <= 0 || EYE_POP_DURATION_SECONDS <= 0) return 1;
  const t = popClock / EYE_POP_DURATION_SECONDS;
  return 1 + Math.sin(t * Math.PI) * EYE_POP_SCALE;
}

export function attachEyeSprite(args: {
  rig: CharacterRig;
  eyeId: EyeId;
  eyeTexture: THREE.Texture;
}): void {
  attachEyeMeshes({
    rig: args.rig,
    headId: 'horse',
    eyeId: args.eyeId,
    expression: 'base',
    eyeTexture: args.eyeTexture,
  });
}

export function disposeEyeSprite(rig: CharacterRig): void {
  disposeEyeMeshes(rig);
  if (rig._eyeSprite == null) return;
  rig._eyeSprite.material.dispose();
  rig._eyeSprite.removeFromParent();
  rig._eyeSprite = null;
}
